import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { ObjectTypes } from '@fi-shared/types';
import { ISiteMapContentItem, ISiteMapContentItemViewModel, ISiteMapTag } from '@fi-shared/models/site-map';
import { SiteMapHelperService } from '@fi-shared/fi-shared-services/site-map-helper.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SiteMapDndType } from '@fi-shared/types/site-map-dnd-types';
import { ISiteMapItemDropEvent, ISiteMapItemPinnedChangeEvent } from '@fi-app/site-map/site-map';

@Component({
    selector: 'fi-site-map-table-new',
    templateUrl: './site-map-table-new.component.html',
    styleUrls: ['./site-map-table-new.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-site-map-table-new' },
    standalone: false,
})
export class SiteMapTableNewComponent implements OnInit, OnDestroy {
    @Input() siteMapTagData: ISiteMapTag;
    protected contentItems: ISiteMapContentItem[] = [];
    @Input() set items(value: ISiteMapContentItem[]) {
        this.contentItems = value || [];
        this.setDataSets();
    }
    @Input() isChildItems: boolean;
    @Input() parentData: ISiteMapContentItem;
    @Input() siteMapViewData: ISiteMapContentItemViewModel[];
    @Input() isViewMode: boolean;
    @Input() isExpanded: boolean = true;
    @Input() tagId: number;
    @Input() isOriginSitemapTable: boolean;

    @Output() elementDrop = new EventEmitter<ISiteMapItemDropEvent>();
    @Output() itemPinnedChange = new EventEmitter<ISiteMapItemPinnedChangeEvent>();

    protected tagsDataSet: ISiteMapContentItem[] = [];
    protected pagesDataSet: ISiteMapContentItem[] = [];
    protected mediaDataSet: ISiteMapContentItem[] = [];
    protected readonly ObjectTypes = ObjectTypes;

    constructor(
        private siteMapHelperService: SiteMapHelperService,
        private cdr: ChangeDetectorRef,
    ) {}

    public ngOnInit(): void {
        this.setDataSets();
        this.subscribeOnDataSetUpdate();
        this.subscribeOnDraggedObjectTypeChange();
    }

    private subscribeOnDataSetUpdate(): void {
        this.siteMapHelperService
            .onSetDataSets$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((tagId: number) => {
                if (tagId === this.tagId) {
                    this.setDataSets();
                }
            });
    }

    protected currentSitemapType: SiteMapDndType;
    private subscribeOnDraggedObjectTypeChange(): void {
        this.siteMapHelperService
            .onCurrentDraggedSitemapTypeChange$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((currentSitemapType: SiteMapDndType) => {
                this.currentSitemapType = currentSitemapType;
                setTimeout(() => this.cdr.markForCheck(), 0);
            });
    }

    protected isDropzoneFallbackVisible(dropListName: string[]): boolean {
        if (this.currentSitemapType === SiteMapDndType.Tag) {
            return dropListName.length === 1 && dropListName[0] === SiteMapDndType.Tag;
        }
        return this.isCurrentSitemapTypeInDropList(dropListName);
    }

    protected isFallbackDropzoneRelativePosition(dropListName: string[]): boolean {
        return (
            !!this.contentItems?.length &&
            this.currentSitemapType === SiteMapDndType.Tag &&
            this.isCurrentSitemapTypeInDropList(dropListName)
        );
    }

    protected isCurrentSitemapTypeInDropList(dropListName: string[]): boolean {
        return dropListName.includes(this.currentSitemapType);
    }

    protected isObjectTypePresentInSiteMap(objectType: ObjectTypes, dataSet: ISiteMapContentItem[]): boolean {
        return dataSet.some((item: ISiteMapContentItem) => {
            return item.ObjectType === objectType;
        });
    }

    private setDataSets(): void {
        this.tagsDataSet = this.getDataSetByObjectType(ObjectTypes.Tag);
        this.pagesDataSet = this.getDataSetByObjectType(ObjectTypes.Document);
        this.mediaDataSet = this.getDataSetByObjectType(ObjectTypes.MediaBank);
    }

    private getDataSetByObjectType(objectType: ObjectTypes): ISiteMapContentItem[] {
        return this.contentItems.filter((item: ISiteMapContentItem) => item.ObjectType === objectType);
    }

    protected onItemDrop($event: ISiteMapItemDropEvent): void {
        this.elementDrop.emit($event);
    }

    protected onItemPinnedChanged($event: ISiteMapItemPinnedChangeEvent): void {
        this.itemPinnedChange.emit($event);
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly SiteMapDndType = SiteMapDndType;
}
